import {
  BenchmarkType,
  IndicatorName,
  PlotReportFactElement,
  PlotReportFactLabelNameEnum,
} from '@/api/rest/resources/types/fact';
import { GraphTileType } from '@/api/rest/resources/types/widget';
import { useNCCardContext } from '@/components/NCCard/NCCard';
import { Logger } from '@/lib/logs/logger';
import { useBenchmarksForOrganization } from '@/pages/shared/hooks/useBenchmarkForFact';
import { useOrganizationIndicatorGraphFact } from '@/pages/shared/hooks/useOrganizationFact';

import { GraphFact } from '../types';
import { kgToTonneFormatter } from '../utils';
// import { useAboveGroundCarbonFacts } from '@/hooks/useAboveGroundCarbonFacts';

const FORMATTERS = {
  [GraphTileType.historic_below_ground_carbon]: kgToTonneFormatter,
  [GraphTileType.historic_above_ground_carbon]: kgToTonneFormatter,
};

const useIndicatorBenchmarFacts = () => {
  // const aboveGroundCarbonFacts = useAboveGroundCarbonFacts();

  return {
    [GraphTileType.historic_below_ground_carbon]: {
      total: IndicatorName.permanent_soil_carbon_total_benchmark,
      average: IndicatorName.permanent_soil_carbon_per_ha_benchmark,
    },
    // [GraphTileType.historic_above_ground_carbon]: {
    //   total: aboveGroundCarbonFacts.total.benchmark,
    //   average: aboveGroundCarbonFacts.perHa.benchmark,
    // },
    [GraphTileType.historic_water_soil_moisture]: {
      total: IndicatorName.water_fc_total_benchmark,
      average: IndicatorName.water_fc_per_ha_benchmark,
    },
    [GraphTileType.protected_on_farm_habitat]: {
      total: IndicatorName.biodiversity_buffer_zone_pct_benchmark_graph,
      average: null,
    },
    [GraphTileType.social_life_expectancy]: {
      total: null,
      average: null,
    },
    [GraphTileType.ndvi]: {
      total: IndicatorName.vegetation_condition_ndvi_benchmark_graph,
      average: null,
    },
    [GraphTileType.historic_deforestation]: {
      total: IndicatorName.deforestation_total_benchmark_graph,
      average: null,
    },
  };
};

const INDICATORS_WITH_BENCHMARKS_WITHOUT_LABELS = [GraphTileType.historic_deforestation];

const useGetBenchmarkFactForIndicator = (indicator: GraphTileType) => {
  const { analysisType } = useNCCardContext();
  const benchmarkFacts = useIndicatorBenchmarFacts();
  const benchmarkFact = benchmarkFacts[indicator as keyof typeof benchmarkFacts]?.[`${analysisType}`] ?? null;

  if (!benchmarkFact) {
    Logger.error(`No benchmark fact defined for indicator: ${indicator} with analysisType: ${analysisType}`);
    throw new Error(`No benchmark fact defined for indicator: ${indicator} with analysisType: ${analysisType}`);
  }

  return benchmarkFact;
};

export const useAggregationBenchmarks = ({
  indicator,
  plotGroupId,
}: {
  indicator: GraphTileType;
  plotGroupId?: string;
}): Partial<Record<BenchmarkType, ReturnType<typeof getFormattedBenchmarkData>>> => {
  const benchmarkFact = useGetBenchmarkFactForIndicator(indicator);

  const fetchWithLabels = !INDICATORS_WITH_BENCHMARKS_WITHOUT_LABELS.includes(indicator);

  /**
   * Some benchmarks need to be requested without labels. In order to prevent duplicating code, so that we don't
   * break the react hook rules, we make the three calls with the same fact and undefined labels.
   * This way the three calls have the same query key, so Tanckstack deduplicates them and makes a single request
   */
  const { [benchmarkFact]: benchmarkHigh } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkFact,
    null,
    fetchWithLabels ? { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.high } : undefined,
    plotGroupId,
  );

  const { [benchmarkFact]: benchmarkModerate } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkFact,
    null,
    fetchWithLabels ? { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.moderate } : undefined,
    plotGroupId,
  );

  const { [benchmarkFact]: benchmarkLow } = useOrganizationIndicatorGraphFact<GraphFact[] | number>(
    benchmarkFact,
    null,
    fetchWithLabels ? { [PlotReportFactLabelNameEnum.benchmark]: BenchmarkType.low } : undefined,
    plotGroupId,
  );

  if (!fetchWithLabels) {
    // All three queries returned the same data (deduplicated). Filter client-side by benchmark label.
    return Object.fromEntries(
      Object.values(BenchmarkType).map((benchmark) => {
        const values = {
          ...benchmarkHigh,
          value: Array.isArray(benchmarkHigh?.value)
            ? (benchmarkHigh?.value as GraphFact[]).filter((fact) => fact.labels?.some((l) => l.value === benchmark))
            : benchmarkHigh?.value,
        } as PlotReportFactElement<GraphFact[] | number | null> | undefined;
        return [benchmark, getFormattedBenchmarkData(values, benchmark)];
      }),
    );
  }

  return {
    high: getFormattedBenchmarkData(benchmarkHigh, BenchmarkType.high),
    moderate: getFormattedBenchmarkData(benchmarkModerate, BenchmarkType.moderate),
    low: getFormattedBenchmarkData(benchmarkLow, BenchmarkType.low),
  };
};

const getFormattedBenchmarkData = (
  benchmarkFactData: PlotReportFactElement<GraphFact[] | null | number> | undefined,
  benchmark: BenchmarkType,
) => {
  const value = Array.isArray(benchmarkFactData?.value)
    ? (benchmarkFactData?.value as GraphFact[]).map((fact) => ({ ...fact, name: `${fact.name}_${benchmark}` }))
    : benchmarkFactData?.value;
  return {
    ...benchmarkFactData,
    name: `${benchmarkFactData?.name}_${benchmark}`,
    value,
  };
};

export const useBenchmarksForAggregation = ({
  indicator,
  plotGroupId,
}: {
  indicator: GraphTileType;
  plotGroupId?: string;
}) => {
  const benchmarkFact = useGetBenchmarkFactForIndicator(indicator);

  const formatter = FORMATTERS[indicator as keyof typeof FORMATTERS];

  return useBenchmarksForOrganization(benchmarkFact, null, formatter, plotGroupId);
};
